import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { FeeStatusBadge, formatINR } from "@/lib/fees";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Wallet, PiggyBank, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Fees() {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [batchId, setBatchId] = useState("");
    const [report, setReport] = useState(null);

    useEffect(() => {
        api.get("/batches").then(r => {
            setBatches(r.data.items);
            if (r.data.items.length) setBatchId(prev => prev || r.data.items[0].id);
        }).catch(() => {});
    }, []);

    const load = async () => {
        if (!batchId) return;
        try {
            const { data } = await api.get(`/fees/batch/${batchId}/report`);
            setReport(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load the fee report");
            setReport(null);
        }
    };
    useEffect(() => { load(); }, [batchId]); // eslint-disable-line

    const exportCsv = async () => {
        try {
            const resp = await api.get(`/fees/batch/${batchId}/report/export`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `fees-${(report?.batch?.name || batchId).replace(/\s+/g, "-")}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Export failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="fees-admin-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Reports</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Fees</h1>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <Select value={batchId} onValueChange={setBatchId}>
                        <SelectTrigger className="w-56" data-testid="fees-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                        <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button className="mx-btn-primary" onClick={exportCsv} disabled={!report} data-testid="fees-export-csv-btn">
                        <Download className="h-4 w-4 mr-2" /> Export CSV
                    </Button>
                </div>
            </div>

            {!report ? (
                <div className="mx-card p-12 text-center text-muted-foreground">
                    {batches.length === 0 ? "No batches available." : "Select a batch to see its fee report."}
                </div>
            ) : (
                <>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <PiggyBank className="h-3.5 w-3.5" /> Collected
                                </div>
                                <div className="font-display text-2xl mt-2" data-testid="total-collected">{formatINR(report.total_collected)}</div>
                            </CardContent>
                        </Card>
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <Wallet className="h-3.5 w-3.5" /> Outstanding
                                </div>
                                <div className="font-display text-2xl mt-2 text-[#E11D48]" data-testid="total-outstanding">{formatINR(report.total_outstanding)}</div>
                            </CardContent>
                        </Card>
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Paid up
                                </div>
                                <div className="font-display text-3xl mt-2" data-testid="paid-count">{report.counts.paid}</div>
                            </CardContent>
                        </Card>
                        <Card className="mx-card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                                    <AlertTriangle className="h-3.5 w-3.5" /> Overdue
                                </div>
                                <div className={`font-display text-3xl mt-2 ${report.counts.overdue ? "text-[#E11D48]" : ""}`} data-testid="overdue-count">
                                    {report.counts.overdue}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Card className="mx-card">
                        <CardContent className="p-0 overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Fee records</TableHead>
                                        <TableHead>Net payable</TableHead>
                                        <TableHead>Paid</TableHead>
                                        <TableHead>Balance</TableHead>
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {report.students.map(s => (
                                        <TableRow key={s.student_id} className="cursor-pointer"
                                            onClick={() => navigate(`/admin/fees/${s.student_id}`)}
                                            data-testid={`fees-row-${s.student_id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="h-9 w-9 rounded-full bg-brand-gradient flex items-center justify-center text-white font-bold text-sm">
                                                        {s.name?.[0] || "?"}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-sm">{s.name}</div>
                                                        <div className="text-xs text-muted-foreground font-mono">{s.enrollment_number || "—"}</div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell><FeeStatusBadge status={s.status} /></TableCell>
                                            <TableCell className="text-sm">{s.structure_count}</TableCell>
                                            <TableCell className="text-sm">{formatINR(s.net_payable)}</TableCell>
                                            <TableCell className="text-sm">{formatINR(s.total_paid)}</TableCell>
                                            <TableCell className="text-sm font-semibold">{s.balance > 0 ? formatINR(s.balance) : "—"}</TableCell>
                                            <TableCell>
                                                <Badge variant="secondary" className="text-[10px]">View</Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {report.students.length === 0 && (
                                        <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">No active students in this batch.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
